import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Comment } from '../../rl-common/comment/comment';
import { DialogService } from '../../rl-common/dialogs/core/dialog.service';
import { AnnotationsStorageService } from '../core/annotations-storage.service';
import { UtilsService } from '../../rl-common/shared/core/utils.service';
import { Visibility } from '../../rl-common/shared/shared/types/visibility.enum';
import { SearchComponent } from '../../rl-common/search/search.component';
import { CommentComponent } from '../../rl-common/comment/comment.component';
import { CustomButton } from '../../rl-common/dialogs/shared/custom-button';
import { SearchResult } from '../../rl-common/shared/shared/types/search-result';
import { EntityType } from '../../rl-common/shared/shared/entity-type.enum';
import { AnnotatorConfig } from '../shared/annotator-config';

@Component({
  selector: 'rl-annotator-comments-panel',
  templateUrl: './annotator-comments-panel.component.html',
  styleUrls: ['./annotator-comments-panel.component.scss', './annotator-comments-panel.component-rtl.css']
})
export class AnnotatorCommentsPanelComponent implements OnInit {
  @Input() allowRefresh: boolean;
  @Input() config: AnnotatorConfig;
  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild(SearchComponent) searchComp: SearchComponent;
  @ViewChildren(CommentComponent) commentsCompList: QueryList<CommentComponent>;

  searchTerm: string;
  showTextEditor: boolean = false;
  comments: Comment[] = [];
  allComments: Comment[] = [];
  hideDeleteAll: boolean = false;
  isCloseable: boolean = true;
  newComment: Comment;
  searchResults: SearchResult[];
  markIndex: number;
  hideCommentsMenu: boolean;
  visibilityTab: Visibility;
  messages: string[] = [];
  actionBtns: CustomButton[] = [];
  busy: boolean = true;
  displayPrivatePublicTabs: boolean;

  constructor(
    private annStorage: AnnotationsStorageService,
    private dialog: DialogService,
    private translate: TranslateService,
    private utils: UtilsService,
    private element: ElementRef,
    private utilsService: UtilsService
  ) {
    this.comments = this.annStorage.commentsArr;

    this.annStorage.comments$.subscribe((cmt) => {
      this.allComments = utils.sort(cmt, 'page', 'offsetTop', 'offsetLeft');

      if (this.displayPrivatePublicTabs) {
        this.setAnnotationsVisibilityCitation(this.visibilityTab ?? Visibility.PRIVATE, true);
      } else {
        this.setAnnotationsVisibilityAssignment(true);
      }

      this.busy = false;
      this.search();
    });
  }

  ngOnInit() {
    this.newComment = new Comment();
    this._subscriveToAnnotationsVisibility();
  }

  _subscriveToAnnotationsVisibility() {
    this.annStorage.getVisibility().subscribe((visibility) => {
      this.setAnnotationsVisibilityCitation(visibility, false);
    });
  }

  setAnnotationsVisibilityCitation(visibility: Visibility, firstTime?: boolean) {
    this.visibilityTab = visibility;
    this.comments = this.allComments.filter((e: Comment) => e.visibility == this.visibilityTab);

    if (!firstTime) {
      this.annStorage.reDrawAll(this.visibilityTab);
    }
  }

  setAnnotationsVisibilityAssignment(firstTime?: boolean) {
    this.comments = this.allComments;
    if (this.comments.length === 0 && this.config.mainAnnotationConfig.entityType === EntityType.ASSIGNMENT && !this.messages.includes('embeddedview.annotations.nocomments')) {
      this.messages.push('embeddedview.annotations.nocomments');
    }

    if (!firstTime) {
      this.annStorage.reDrawAll();
    }
  }

  selectedCommentId: string;
  hoverCommentId: string;
  @HostListener('lgntoann.comment.select', ['$event'])
  toggleSelectComment(e: CustomEvent | string) {
    if (!e) return;
    typeof e === 'string' ? this.annStorage.scrollToAnnotation(e) : this.selectComment(e.detail);
    let commentAnnId = typeof e === 'string' ? e : e.detail;
    this.commentsCompList.toArray()[this.findCommentIndex(commentAnnId)].openReplies();
  }

  @HostListener('lgntoann.comment.hover', ['$event'])
  toggleHoverComment(e: CustomEvent | string) {
    let idToCancel = this.hoverCommentId;
    this.hoverCommentId = typeof e === 'object' ? e.detail : e;
    if (idToCancel) {
      this.annStorage.unhoverAnnotation(idToCancel);
      if (idToCancel == this.hoverCommentId) return (this.hoverCommentId = '');
    }

    this.annStorage.hoverAnnotation(this.hoverCommentId);
  }

  isVisibilitySelected(value: string) {
    return this.visibilityTab === value;
  }

  deleteFromPdf(e: CustomEvent) {
    this.commentsCompList.toArray()[this.findCommentIndex(e.detail.id)].delete();
  }

  findCommentIndex = (annId) => this.comments.findIndex((a) => a.attachedId == annId);

  close() {
    this.onClose.emit();
    this.annStorage.deleteNewComment();
  }

  refresh() {
    if (this.busy) return;
    this.annStorage.reload();
    this.busy = true;
  }

  cancel(i: number) {
    this.annStorage.cancelEditComment(this.comments[i]);
  }

  save(comment: Comment, i: number) {
    this.annStorage.saveComment(comment);
    this.saveLastCommentDetails(comment);
  }

  saveLastCommentDetails(comment: Comment) {
    this.newComment = new Comment();
    this.newComment.visibility = this.visibilityTab;
  }

  changeVisibility(i: number, visibility: Visibility) {
    this.comments[i].visibility = visibility;
    this.commentsCompList.toArray()[i].refresh();
    this.annStorage.saveComment(this.comments[i]);
  }

  editComment = (i: number) => this.annStorage.editComment(i);

  deleteComment = (index: number, comment: Comment) => {
    this.annStorage.deleteComment(comment);
    if (!comment.parentAttachedId) {
      this.comments.splice(index, 1);
    }
    this._focusId('open-comments-panel');
  };

  cancelComment = (id: string) => {
    this._focusId(`menu-btn-comment-actions-${id}`);
  };

  private _focusId(id: string) {
    const elementToFocus = document.querySelector(`#${id}`);
    if (elementToFocus != null) {
      (elementToFocus as HTMLElement).focus();
    }
  }

  deleteAll() {
    let msg = this.translate.instant('are.you.sure.delete.all.comments');
    this.dialog.openDeleteDialog(msg, () => this.annStorage.deleteAllComments());
  }

  selectComment = (annId: string) => {
    if (!annId) return;
    this.utils.scroll(this.element.nativeElement.querySelector(`li[annid='${annId}']`), '.comment-wrapper-inner');
    this.commentsCompList.toArray()[this.findCommentIndex(annId)].openReplies();
  };

  search() {
    this.markIndex = undefined;
    this.searchResults = [];

    if (!this.searchTerm) return;

    this.searchResults = this.utils.search(this.comments, 'subComments', this.searchTerm, ['user.displayName', 'title']);
    this.searchComp.totalResults = this.searchResults.length;

    this.markIndex = 0;
    this.markResult();
  }
  markResult() {
    if (!this.searchResults || this.searchResults.length == 0) {
      return;
    }
    //unmark marked result
    if (this.markIndex != undefined) {
      this.searchResults[this.markIndex].strMarker.markerClass = 'search-highlighted';
    }

    this.markIndex = this.searchComp.resultsNum - 1;
    this.searchResults[this.markIndex].strMarker.markerClass = 'search-highlighted-active';

    let objectIndex = this.searchResults[this.markIndex].objectIndex;
    let parentCommentIndex = typeof objectIndex == 'string' ? objectIndex.split(',')[0] : objectIndex;
    let scrollToAnn = this.annStorage.getCommentAnnotation(parentCommentIndex);
    this.selectComment(scrollToAnn?.id);
  }

  disableEditComments() {
    this.hideCommentsMenu = true;
  }

  createNewSubAnnotation(comment: Comment, subComment: Comment) {
    this.annStorage.reply(comment.attachedId, subComment.title).subscribe((ann) => {
      comment.subComments.push(Object.assign(new Comment(ann), { parentAttachedId: comment.attachedId }));
      this.utilsService.sortComplex(comment.subComments, [{ field: 'date', dir: 'asc' }]);
    });
  }
}
